import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Stack,
  Button,
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  CircularProgress,
  Alert
} from '@material-ui/core';
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import { UserListHead, UserListToolbar } from '../components/user';
import AddCompanyDialog from '../components/billing/AddCompanyDialog';
import DeleteCompanyButton from '../components/billing/DeleteCompanyButton';
import EditCompanyButton from '../components/billing/EditCompanyButton';
import CompanyAdminsButton from '../components/billing/CompanyAdminsButton';
import useVrackApi from '../hooks/useVrackApi';

const TABLE_HEAD = [
  { id: 'name', label: 'Company', alignRight: false },
  { id: 'id', label: 'Company ID', alignRight: false },
  { id: 'type', label: 'Company Type', alignRight: false },
  { id: 'contactEmail', label: 'Company Contact Email', alignRight: false },
  { id: 'teamsCount', label: 'Teams', alignRight: false },
  { id: 'actions', label: '', alignRight: true }
];

// Map the snake_case `CompanyResponse` shape returned by /companies into
// the camelCase shape the table already consumes. Defensive against the
// endpoint returning either a bare array or an object with an `items`
// field, the same way the previous useCompanies hook did.
const resolveCompanyItems = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};

const mapCompaniesResponse = (response) =>
  resolveCompanyItems(response).map((item) => ({
    id: item.company_id,
    name: item.name || '-',
    type: item.type,
    contactEmail: item.contact_email,
    teamsCount: item.team_count ?? 0
  }));

export default function AdminCompanies() {
  const { loading, error, fetchData } = useVrackApi('get', '/companies');
  const [companies, setCompanies] = useState([]);

  const fetchCompanies = useCallback(
    async (signal) => {
      try {
        const response = await fetchData({ signal });
        if (!signal?.aborted) {
          setCompanies(mapCompaniesResponse(response));
        }
      } catch (err) {
        if (signal?.aborted || err?.code === 'ERR_CANCELED') return;
        console.error('Failed to fetch companies:', err);
        setCompanies([]);
      }
    },
    [fetchData]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchCompanies(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filteredCompanies = useMemo(() => {
    if (!filterName) return companies;
    const searchLower = filterName.toLowerCase();
    return companies.filter(
      (company) =>
        company.name.toLowerCase().includes(searchLower) ||
        company.id.toLowerCase().includes(searchLower) ||
        (company.contactEmail || '').toLowerCase().includes(searchLower)
    );
  }, [companies, filterName]);

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Re-trigger the GET without args (used as the refetch callback for the
  // mutation children after a successful add/update/delete). The page
  // doesn't track the signal here — the unmount cleanup from the effect
  // above handles cancellation.
  const refetch = () => fetchCompanies(undefined);

  const isNotFound = filteredCompanies.length === 0;

  const renderTableBody = () => (
    <Card>
      <UserListToolbar
        placeholder="Search company, Company ID, or contact email..."
        filterName={filterName}
        onFilterName={handleFilterByName}
      />
      <Scrollbar>
        <TableContainer sx={{ minWidth: 900 }}>
          <Table>
            <UserListHead
              order="asc"
              orderBy="name"
              headLabel={TABLE_HEAD}
              rowCount={companies.length}
              onRequestSort={() => {}}
            />
            <TableBody>
              {filteredCompanies
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow hover key={row.id}>
                    <TableCell align="left">
                      <RouterLink
                        to={`/billing/company/${row.id}`}
                        style={{
                          color: '#0067b2',
                          fontWeight: 600,
                          textDecoration: 'none'
                        }}
                      >
                        {row.name}
                      </RouterLink>
                    </TableCell>
                    <TableCell align="left">{row.id}</TableCell>
                    <TableCell align="left">{row.type}</TableCell>
                    <TableCell align="left">{row.contactEmail}</TableCell>
                    <TableCell align="left">{row.teamsCount}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <EditCompanyButton company={row} onUpdated={refetch} />
                        <CompanyAdminsButton company={row} />
                        <DeleteCompanyButton company={row} onDeleted={refetch} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              {isNotFound && (
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                    <SearchNotFound searchQuery={filterName} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={companies.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Card>
  );

  const renderError = () => (
    <Alert severity="error" sx={{ mb: 2 }}>
      Failed to load companies.{' '}
      <Typography
        component="span"
        sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main' }}
        onClick={refetch}
      >
        Retry
      </Typography>
    </Alert>
  );

  const renderLoading = () => (
    <Stack alignItems="center" sx={{ py: 8 }}>
      <CircularProgress />
    </Stack>
  );

  const renderContent = () => {
    if (error) return renderError();
    if (loading && companies.length === 0) return renderLoading();
    return renderTableBody();
  };

  return (
    <Page title="Admin | Companies | Virtual Racks">
      <AddCompanyDialog
        isOpen={addModalOpen}
        closeHandler={() => setAddModalOpen(false)}
        onAdded={refetch}
      />
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
            >
              <Stack spacing={1}>
                <Typography variant="h4">Companies</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Create and manage companies available in the billing platform.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button variant="contained" onClick={() => setAddModalOpen(true)}>
                  New Company
                </Button>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            {renderContent()}
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
